import { Icon } from '@buggy/shared'
import { Button } from 'antd'
import cloneDeep from 'lodash-es/cloneDeep'
import isEmpty from 'lodash-es/isEmpty'
import isUndefined from 'lodash-es/isUndefined'
import { FunctionComponent } from 'react'
import styled from 'styled-components'
import { initialPostFilterConditions } from '@/components/kol/detail/creative-content-analyses/post-list'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import { type KolDetailPostFilterConditions } from '@/hooks/use-authorization/routes'
import useCustomizedTag from '@/hooks/use-customized-tag'
import { useIntl } from '@/i18n/hooks/use-intl'
import { ampli } from '@/utils/ampli'
import { PlatformShortcode } from '@/utils/convert-platform'
import getPostTypeLabel from '@/utils/detail/get-post-type-label'

interface Tag {
  label: string
  fields: (keyof KolDetailPostFilterConditions)[]
}

interface PostListSearchTagProps {
  totalCount: number
}

const PostListSearchTag: FunctionComponent<PostListSearchTagProps> = ({
  totalCount,
}) => {
  const { formatMessage, formatDateTimeRange } = useIntl()
  const { searchParams, setSearchParams } = useKolDetailRoute()
  const { data } = searchParams
  const { getCustomizedTagLocalizedName } = useCustomizedTag()

  const selectedTags = ((): Tag[] => {
    const tags: Tag[] = []

    if (!!data?.keyword) {
      tags.push({
        label: `${formatMessage({ id: 'search:input_keyword' })}：${
          data.keyword
        }`,
        fields: ['keyword'],
      })
    }

    if (!!data?.postListPostTypes) {
      const platform = data?.postListPlatform ?? data?.platform ?? ''

      const postTypeLabels = data.postListPostTypes
        .map((type) => getPostTypeLabel(type, platform, formatMessage))
        .filter(Boolean)

      const uniquePostTypeLabels = Array.from(new Set(postTypeLabels))

      tags.push({
        label: `${formatMessage({ id: 'kol:content_filter_post_type' })}${formatMessage({ id: 'general:colon' })}${uniquePostTypeLabels.join(', ')}`,
        fields: ['postListPostTypes'],
      })
    }

    if (!!data?.postListCustomizedTagsNames) {
      tags.push({
        label: data.postListCustomizedTagsNames
          .map((type) => getCustomizedTagLocalizedName(type))
          .join(', '),
        fields: ['postListCustomizedTagsNames'],
      })
    }

    if (!!data?.postListDateRangeStart && !!data?.postListDateRangeEnd) {
      tags.push({
        label: `${formatMessage({
          id: 'search:filter_publish_time_label',
        })}${formatMessage({ id: 'general:colon' })}${formatDateTimeRange(data.postListDateRangeStart, data.postListDateRangeEnd)}`,
        fields: ['postListDateRangeStart', 'postListDateRangeEnd'],
      })
    }

    return tags
  })()

  const handleTagClose = async (fields: string[]): Promise<void> => {
    const newSearchParams = cloneDeep(data)

    if (!isEmpty(newSearchParams)) {
      fields.forEach((field) => {
        if (field in newSearchParams) {
          delete newSearchParams[field]
        }
      })

      const platform =
        newSearchParams?.postListPlatform ?? newSearchParams?.platform

      ampli.searchForCreativeContentInKd({
        topicTypes: newSearchParams.postListCustomizedTagsNames,
        keyword: newSearchParams.keyword,
        aiEnabled: !isUndefined(newSearchParams.postListShowSponsoredPost),
        platform: platform === PlatformShortcode.All ? 'All' : platform,
        postTypes: newSearchParams.postListPostTypes,
        postStartDate: newSearchParams.postListDateRangeStart,
        postEndDate: newSearchParams.postListDateRangeEnd,
      })

      await setSearchParams(newSearchParams, 'postList')
    }
  }

  const resetAll = async (): Promise<void> => {
    ampli.searchForCreativeContentInKd({
      topicTypes: undefined,
      keyword: undefined,
      aiEnabled: !isUndefined(data?.postListShowSponsoredPost),
      platform:
        data?.platform === PlatformShortcode.All ? 'All' : data?.platform,
      postTypes: undefined,
      postStartDate: undefined,
      postEndDate: undefined,
    })

    await setSearchParams(
      {
        ...initialPostFilterConditions,
        platform: data?.platform,
        postListPlatform: PlatformShortcode.All,
        postListShowSponsoredPost: data?.postListShowSponsoredPost,
      },
      'postList',
    )
  }

  return (
    <Wrapper>
      <SearchTagWrapper>
        {formatMessage(
          { id: 'search:result_count' },
          { resultCount: totalCount },
        )}
        {formatMessage({ id: 'general:comma' })}{' '}
        {formatMessage({ id: 'kol:case_list_active_filter_title' })}
        {selectedTags.map(({ label, fields }) => (
          <TagWrapper key={label}>
            <span>{label}</span>

            <StyledButton type='text' onClick={() => handleTagClose(fields)}>
              <Icon name='x' />
            </StyledButton>
          </TagWrapper>
        ))}
        {selectedTags.length > 0 && (
          <AdvancedFilterButton size='small' type='link' onClick={resetAll}>
            {formatMessage({ id: 'general:reset_system_default_btn' })}
          </AdvancedFilterButton>
        )}
      </SearchTagWrapper>
    </Wrapper>
  )
}

export default PostListSearchTag

const Wrapper = styled.div`
  width: 100%;
`

const SearchTagWrapper = styled.div`
  font-size: 14px;
  line-height: 16px;
  color: ${({ theme }): string => theme.colors.text.primary};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 8px;
  column-gap: 8px;

  @media (max-width: 767px) {
    padding: 0 20px;
  }
`

const TagWrapper = styled.div`
  display: flex;
  align-items: center;
  min-height: 32px;
  padding: 5px 10px;
  font-size: 13px;
  border-radius: 4px;
  border: 1px solid ${({ theme }): string => theme.colors.border.border};
  background: ${({ theme }): string => theme.colors.background.white};
  color: ${({ theme }): string => theme.colors.text.secondary};
  line-height: 22px;
  box-sizing: border-box;
  outline: none;
  gap: 7px;
`

const AdvancedFilterButton = styled(Button)`
  padding: 0;
  span {
    text-decoration-line: underline;
    text-underline-offset: 2px;
  }
`

const StyledButton = styled(Button)`
  height: 100%;
  padding: 0;
  background-color: transparent;
`
